import { Amplify } from "aws-amplify";
import {
  signIn as amplifySignIn,
  signOut as amplifySignOut,
  confirmSignIn,
  fetchAuthSession,
  getCurrentUser,
} from "aws-amplify/auth";

// Config injected at deploy time via config.js → window.__BB_CONFIG__
declare const window: Window & {
  __BB_CONFIG__?: {
    apiUrl?: string;
    apiKey?: string;
    cognitoUserPoolId?: string;
    cognitoClientId?: string;
  };
};

let configured = false;

export function configureAuth(): void {
  if (configured) return;
  const cfg = window.__BB_CONFIG__;
  if (!cfg?.cognitoUserPoolId || !cfg?.cognitoClientId) {
    throw new Error("Cognito config missing from config.js");
  }
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: cfg.cognitoUserPoolId,
        userPoolClientId: cfg.cognitoClientId,
      },
    },
  });
  configured = true;
}

export async function signIn(
  username: string,
  password: string
): Promise<{ needsNewPassword: boolean }> {
  configureAuth();
  const result = await amplifySignIn({ username, password });

  if (
    result.nextStep?.signInStep === "CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED"
  ) {
    return { needsNewPassword: true };
  }

  return { needsNewPassword: false };
}

export async function completeNewPassword(
  newPassword: string
): Promise<void> {
  await confirmSignIn({ challengeResponse: newPassword });
}

export async function getIdToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export async function getUsername(): Promise<string> {
  try {
    const user = await getCurrentUser();
    return user.signInDetails?.loginId ?? user.username;
  } catch {
    return "";
  }
}

export async function isLoggedIn(): Promise<boolean> {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}

export async function signOut(): Promise<void> {
  await amplifySignOut();
}
